import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ShieldCheck, Loader2, Lock, Sparkles, Clock, CalendarCheck, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { pauseSmoothScroll, resumeSmoothScroll } from "@/components/SmoothScroll";

const HELP_ME_PICK = "Not sure yet — help me pick";
const OWN_IDEA = "My own idea";
const RESEND_COOLDOWN = 30; // seconds
const RAZORPAY_SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";
const PHONE_PATTERN = /^\+?[0-9]{7,15}$/;
const OTP_PATTERN = /^[0-9]{4,8}$/;
const isValidPhone = (phone) => PHONE_PATTERN.test(phone.replace(/[\s-]/g, ""));

let otpStatusCache;
async function getOtpStatus() {
  if (otpStatusCache) return otpStatusCache;
  try {
    const { data } = await api.get("/otp/status");
    otpStatusCache = { enabled: !!data.enabled, whatsappEnabled: !!data.whatsapp_enabled };
  } catch {
    otpStatusCache = { enabled: false, whatsappEnabled: false };
  }
  return otpStatusCache;
}

let razorpayScriptPromise;
function loadRazorpayScript() {
  if (window.Razorpay) return Promise.resolve(true);
  if (razorpayScriptPromise) return razorpayScriptPromise;
  razorpayScriptPromise = new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = RAZORPAY_SCRIPT_SRC;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
  return razorpayScriptPromise;
}

// Self-contained payment modal (own OTP flow, doesn't share state with
// EnquiryDialog) — collects the same full enquiry details as the regular
// enquiry form (interest, qualifying questions, message), submits that as a
// real enquiry first so the team has full context, then only once that
// succeeds creates a Razorpay order, opens Checkout, and verifies the
// payment signature server-side on success.
export default function PaymentDialog({ open, onClose, course, plan }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [interest, setInterest] = useState("");
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [phoneError, setPhoneError] = useState("");

  const [otpEnabled, setOtpEnabled] = useState(false);
  const [otpWhatsappEnabled, setOtpWhatsappEnabled] = useState(false);
  const [otpChannel, setOtpChannel] = useState("sms"); // "sms" | "whatsapp" — whichever was last sent
  const [waPendingToken, setWaPendingToken] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpToken, setOtpToken] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const verifiedPhoneRef = useRef("");

  useEffect(() => {
    getOtpStatus().then(({ enabled, whatsappEnabled }) => {
      setOtpEnabled(enabled);
      setOtpWhatsappEnabled(whatsappEnabled);
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    pauseSmoothScroll();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      resumeSmoothScroll();
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  useEffect(() => {
    if (otpVerified && form.phone !== verifiedPhoneRef.current) {
      setOtpVerified(false);
      setOtpToken("");
    }
  }, [form.phone, otpVerified]);

  useEffect(() => {
    if (!open) {
      setForm({ name: "", email: "", phone: "", message: "" });
      setInterest("");
      setAnswers({});
      setError("");
      setPhoneError("");
      setOtpSent(false);
      setOtpVerified(false);
      setOtpToken("");
      setOtpCode("");
      setOtpError("");
      setOtpChannel("sms");
      setWaPendingToken("");
      verifiedPhoneRef.current = "";
    }
  }, [open]);

  // Verification is required once either channel is configured — WhatsApp is
  // an alternate delivery path for the same verification step, not a
  // separate optional check.
  const otpRequired = otpEnabled || otpWhatsappEnabled;

  const projectChoices = course?.projects?.map((p) => p.title) || [];
  const interestChoices = projectChoices.length > 0 ? [...projectChoices, OWN_IDEA, HELP_ME_PICK] : [];

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const updPhone = (e) => {
    const digitsOnly = e.target.value.replace(/[^0-9+\s-]/g, "");
    setForm((f) => ({ ...f, phone: digitsOnly }));
    setPhoneError("");
  };

  const sendCode = async (channel = "sms") => {
    if (!form.phone || cooldown > 0) return;
    if (!isValidPhone(form.phone)) {
      setPhoneError("Enter a valid phone number (digits only, with country code).");
      return;
    }
    setPhoneError("");
    setOtpError("");
    setOtpSending(true);
    try {
      if (channel === "whatsapp") {
        const { data } = await api.post("/otp/send-whatsapp", { phone: form.phone });
        setWaPendingToken(data.pending_token);
      } else {
        await api.post("/otp/send", { phone: form.phone });
      }
      setOtpChannel(channel);
      setOtpSent(true);
      setOtpCode("");
      setCooldown(RESEND_COOLDOWN);
      toast.success("Code sent", {
        description: channel === "whatsapp"
          ? `We sent a WhatsApp code to ${form.phone}.`
          : `We texted a verification code to ${form.phone}.`,
      });
    } catch (err) {
      setOtpError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't send a code. Please try again.");
    } finally {
      setOtpSending(false);
    }
  };

  const verifyCode = async () => {
    if (!OTP_PATTERN.test(otpCode)) {
      setOtpError("Enter the code we sent you (4–8 digits).");
      return;
    }
    setOtpError("");
    setOtpVerifying(true);
    try {
      const { data } = otpChannel === "whatsapp"
        ? await api.post("/otp/verify-whatsapp", { phone: form.phone, code: otpCode, pending_token: waPendingToken })
        : await api.post("/otp/verify", { phone: form.phone, code: otpCode });
      setOtpVerified(true);
      setOtpToken(data.token);
      verifiedPhoneRef.current = form.phone;
      toast.success("Phone verified");
    } catch (err) {
      setOtpError(formatApiErrorDetail(err.response?.data?.detail) || "That code didn't match.");
    } finally {
      setOtpVerifying(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isValidPhone(form.phone)) {
      setPhoneError("Enter a valid phone number (digits only, with country code).");
      return;
    }
    setPhoneError("");
    if (otpRequired && !otpVerified) {
      setError("Please verify your phone number before paying.");
      return;
    }
    setSubmitting(true);

    // Step 1: submit the same full enquiry the regular form collects, so the
    // team has interest/answers/message on record even though this visitor
    // is paying online — not a stripped-down version of that form.
    let leadId;
    try {
      const qaLines = (course?.qualifying_questions || [])
        .filter((q) => answers[q.key])
        .map((q) => `${q.question} ${answers[q.key]}`);
      const composedMessage = [
        `Paying online: ${plan.label} — ${plan.amount_display}`,
        interest ? `Interested in: ${interest}` : null,
        ...qaLines,
        form.message || null,
      ]
        .filter(Boolean)
        .join("\n\n");
      const { data: enquiry } = await api.post("/enquiries", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: composedMessage,
        course_slug: course?.slug || null,
        course_title: course?.title || null,
        source: `landing:payment:${plan.key}`,
        otp_token: otpRequired ? otpToken : undefined,
      });
      leadId = enquiry?.lead_id;
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't save your details. Please try again.");
      setSubmitting(false);
      return;
    }

    // Step 2: only once the enquiry is on record, start the actual payment —
    // carrying the same lead_id forward so the Razorpay order (and anyone
    // looking at it in the Razorpay dashboard) can be traced back to this
    // exact enquiry.
    try {
      const { data: order } = await api.post("/payments/create-order", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        course_slug: course.slug,
        plan_key: plan.key,
        otp_token: otpRequired ? otpToken : undefined,
        lead_id: leadId,
      });

      const scriptOk = await loadRazorpayScript();
      if (!scriptOk || !window.Razorpay) {
        setError("Couldn't load the payment window. Please check your connection and try again.");
        setSubmitting(false);
        return;
      }

      // Tracks whether this checkout attempt already reached a final state
      // (success or a reported failure), so a modal close after either one
      // doesn't also fire a duplicate "failed" notification.
      let settled = false;

      const notifyFailed = (reason, razorpayIds = {}) => {
        if (settled) return;
        settled = true;
        api.post("/payments/failed", {
          name: form.name,
          email: form.email,
          phone: form.phone,
          course_slug: course.slug,
          plan_key: plan.key,
          lead_id: leadId,
          reason,
          ...razorpayIds,
        }).catch(() => {});
      };

      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "Fluenex Academy",
        description: `${order.course_title} — ${order.plan.label}`,
        prefill: { name: form.name, email: form.email, contact: form.phone },
        theme: { color: "#E4633F" },
        handler: async (response) => {
          settled = true;
          try {
            const { data: result } = await api.post("/payments/verify", {
              name: form.name,
              email: form.email,
              phone: form.phone,
              course_slug: course.slug,
              plan_key: plan.key,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              lead_id: leadId,
            });
            toast.success("Payment received", {
              description: result.pending_amount
                ? `We'll follow up separately to collect the remaining ₹${result.pending_amount}.`
                : "You're all set — we'll be in touch with next steps.",
            });
            onClose();
          } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || "Payment could not be verified. If money was deducted, contact us and we'll sort it out.");
          } finally {
            setSubmitting(false);
          }
        },
        modal: {
          ondismiss: () => {
            notifyFailed("Checkout closed before completing payment");
            setSubmitting(false);
          },
        },
      });
      rzp.on("payment.failed", (response) => {
        notifyFailed(response.error?.description || response.error?.reason || "Payment failed", {
          razorpay_order_id: response.error?.metadata?.order_id,
          razorpay_payment_id: response.error?.metadata?.payment_id,
        });
        setError("Payment failed. Please try again or use a different payment method.");
        setSubmitting(false);
      });
      rzp.open();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Please try again.");
      setSubmitting(false);
    }
  };

  const canSubmit = !submitting && (!otpRequired || otpVerified) && form.name && form.email && isValidPhone(form.phone);

  if (!plan) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="payment-dialog"
          className="fixed inset-0 z-[70] flex items-end md:items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div
            className="absolute inset-0 bg-ink/60 backdrop-blur-sm"
            onClick={onClose}
            data-testid="payment-backdrop"
          />
          <motion.div
            className="relative w-full md:max-w-lg max-h-[90vh] md:max-h-[85vh] bg-surface rounded-t-3xl md:rounded-xl border border-line shadow-lg overflow-hidden flex flex-col"
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="shrink-0 flex items-start justify-between p-6 md:p-8 border-b border-line">
              <div className="pr-6">
                <p className="font-mono text-[10px] uppercase tracking-widest text-terracotta mb-2">
                  {plan.label} · secure payment
                </p>
                <h3 className="font-display font-semibold tracking-tight text-2xl md:text-3xl leading-tight">
                  {course?.title}
                </h3>
                <div className="flex items-center gap-2 flex-wrap mt-3">
                  <p className="font-mono text-[11px] uppercase tracking-widest text-emerald">
                    Pay {plan.amount_display} now
                  </p>
                  {plan.duration && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-ink text-white px-2.5 py-1 font-sans text-[10px] font-black uppercase tracking-wide">
                      <Clock size={10} /> {plan.duration}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={onClose}
                data-testid="payment-close"
                className="p-1 hover:opacity-60 transition-opacity"
                aria-label="Close"
              >
                <X size={22} />
              </button>
            </div>

            <form onSubmit={submit} className="p-6 md:p-8 space-y-4 overflow-y-auto" data-lenis-prevent>
              {plan.note && (
                <p className="text-[13.5px] text-graphite leading-relaxed bg-stone rounded-xl px-4 py-3">{plan.note}</p>
              )}

              <Field label="Your name" testId="payment-name">
                <input
                  required
                  value={form.name}
                  onChange={upd("name")}
                  data-testid="payment-name-input"
                  className="w-full bg-white text-ink rounded-lg border border-line px-4 py-3 outline-none focus:border-emerald focus:ring-1 focus:ring-emerald transition-colors"
                  placeholder="Full name"
                />
              </Field>

              {interestChoices.length > 0 && (
                <Field label="Which idea interests you? (optional)" testId="payment-interest">
                  <div className="flex flex-wrap gap-2">
                    {interestChoices.map((choice) => {
                      const isPicked = interest === choice;
                      return (
                        <button
                          key={choice}
                          type="button"
                          onClick={() => setInterest(isPicked ? "" : choice)}
                          data-testid={`payment-interest-${choice.replace(/\s+/g, "-").toLowerCase()}`}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-[13px] font-semibold transition-colors ${
                            isPicked
                              ? "bg-emerald text-white border-emerald"
                              : "border-line text-ink/80 hover:border-emerald"
                          }`}
                        >
                          {isPicked && <Sparkles size={12} />}
                          {choice}
                        </button>
                      );
                    })}
                  </div>
                </Field>
              )}

              {course?.qualifying_questions?.length > 0 && (
                <div className="space-y-4 pt-1 pb-1 border-t border-line/70">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-terracotta pt-4">
                    Help us prepare for your call (optional)
                  </p>
                  {course.qualifying_questions.map((q) => (
                    <Field key={q.key} label={q.question} testId={`payment-q-${q.key}`}>
                      <div className="space-y-2">
                        {q.options.map((opt) => {
                          const isPicked = answers[q.key] === opt;
                          return (
                            <RadioCard
                              key={opt}
                              label={opt}
                              selected={isPicked}
                              testId={`payment-q-${q.key}-${opt.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                              onClick={() =>
                                setAnswers((a) => ({ ...a, [q.key]: isPicked ? undefined : opt }))
                              }
                            />
                          );
                        })}
                      </div>
                    </Field>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Email" testId="payment-email">
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={upd("email")}
                    data-testid="payment-email-input"
                    className="w-full bg-white text-ink rounded-lg border border-line px-4 py-3 outline-none focus:border-emerald focus:ring-1 focus:ring-emerald transition-colors"
                    placeholder="you@email.com"
                  />
                </Field>
                <Field label="Phone" testId="payment-phone">
                  <div className="flex gap-2">
                    <input
                      required
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      pattern="\+?[0-9]{7,15}"
                      value={form.phone}
                      onChange={updPhone}
                      data-testid="payment-phone-input"
                      disabled={otpVerified}
                      className="w-full bg-white text-ink rounded-lg border border-line px-4 py-3 outline-none focus:border-emerald focus:ring-1 focus:ring-emerald transition-colors disabled:opacity-60"
                      placeholder="+91 98765 43210"
                    />
                    {!otpVerified && !otpSent && otpEnabled && (
                      <button
                        type="button"
                        onClick={() => sendCode("sms")}
                        disabled={!form.phone || otpSending || cooldown > 0}
                        data-testid="payment-otp-send"
                        className="shrink-0 rounded-lg border border-emerald text-emerald font-semibold text-xs px-3 whitespace-nowrap hover:bg-emerald/5 transition-colors disabled:opacity-50 disabled:hover:bg-transparent"
                      >
                        {otpSending && otpChannel === "sms" ? <Loader2 size={14} className="animate-spin mx-auto" /> : "Send code"}
                      </button>
                    )}
                    {!otpVerified && !otpSent && otpWhatsappEnabled && (
                      <button
                        type="button"
                        onClick={() => sendCode("whatsapp")}
                        disabled={!form.phone || otpSending || cooldown > 0}
                        data-testid="payment-otp-send-whatsapp"
                        title="Send OTP on WhatsApp"
                        className="shrink-0 rounded-lg border border-emerald text-emerald px-3 whitespace-nowrap hover:bg-emerald/5 transition-colors disabled:opacity-50 disabled:hover:bg-transparent"
                      >
                        {otpSending && otpChannel === "whatsapp" ? <Loader2 size={14} className="animate-spin mx-auto" /> : <MessageCircle size={16} />}
                      </button>
                    )}
                    {!otpVerified && otpSent && (
                      <button
                        type="button"
                        onClick={() => sendCode(otpChannel)}
                        disabled={!form.phone || otpSending || cooldown > 0}
                        data-testid="payment-otp-resend"
                        className="shrink-0 rounded-lg border border-emerald text-emerald font-semibold text-xs px-3 whitespace-nowrap hover:bg-emerald/5 transition-colors disabled:opacity-50 disabled:hover:bg-transparent"
                      >
                        {otpSending ? (
                          <Loader2 size={14} className="animate-spin mx-auto" />
                        ) : cooldown > 0 ? (
                          `Resend (${cooldown}s)`
                        ) : (
                          "Resend code"
                        )}
                      </button>
                    )}
                    {otpVerified && (
                      <span
                        data-testid="payment-otp-verified"
                        className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-emerald/10 text-emerald font-semibold text-xs px-3"
                      >
                        <ShieldCheck size={14} /> Verified
                      </span>
                    )}
                  </div>
                  {otpSent && !otpVerified && (
                    <p className="font-sans text-[11px] text-graphite mt-1.5">
                      Code sent via {otpChannel === "whatsapp" ? "WhatsApp" : "SMS"}.{" "}
                      {otpChannel === "sms" && otpWhatsappEnabled && cooldown === 0 && (
                        <button type="button" onClick={() => sendCode("whatsapp")} className="text-emerald font-bold underline underline-offset-2">
                          Try WhatsApp instead
                        </button>
                      )}
                      {otpChannel === "whatsapp" && otpEnabled && cooldown === 0 && (
                        <button type="button" onClick={() => sendCode("sms")} className="text-emerald font-bold underline underline-offset-2">
                          Try SMS instead
                        </button>
                      )}
                    </p>
                  )}
                  {phoneError && (
                    <p data-testid="payment-phone-error" className="text-sm text-destructive font-mono mt-2">
                      {phoneError}
                    </p>
                  )}
                </Field>
              </div>

              {(otpEnabled || otpWhatsappEnabled) && otpSent && !otpVerified && (
                <Field label="Verification code" testId="payment-otp-code">
                  <div className="flex gap-2">
                    <input
                      required
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                      inputMode="numeric"
                      pattern="[0-9]{4,8}"
                      minLength={4}
                      maxLength={8}
                      data-testid="payment-otp-code-input"
                      className="w-full bg-white text-ink rounded-lg border border-line px-4 py-3 outline-none focus:border-emerald focus:ring-1 focus:ring-emerald transition-colors tracking-[0.3em]"
                      placeholder="6-digit code"
                    />
                    <button
                      type="button"
                      onClick={verifyCode}
                      disabled={!OTP_PATTERN.test(otpCode) || otpVerifying}
                      data-testid="payment-otp-verify"
                      className="shrink-0 rounded-lg bg-emerald text-white font-semibold text-sm px-5 hover:bg-emeraldhover transition-colors disabled:opacity-50"
                    >
                      {otpVerifying ? <Loader2 size={16} className="animate-spin" /> : "Verify"}
                    </button>
                  </div>
                  {otpError && (
                    <p data-testid="payment-otp-error" className="text-sm text-destructive font-mono mt-2">
                      {otpError}
                    </p>
                  )}
                </Field>
              )}

              <Field label="Anything you'd like us to know (optional)" testId="payment-message">
                <textarea
                  rows={3}
                  value={form.message}
                  onChange={upd("message")}
                  data-testid="payment-message-input"
                  className="w-full bg-white text-ink rounded-lg border border-line px-4 py-3 outline-none focus:border-emerald focus:ring-1 focus:ring-emerald transition-colors"
                  placeholder="Your background, timing, questions…"
                />
              </Field>

              {error && (
                <p data-testid="payment-error" className="text-sm text-destructive font-mono">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={!canSubmit}
                data-testid="payment-submit"
                className="w-full rounded-full bg-terracotta text-white font-semibold text-base px-8 py-4 shadow-md hover:bg-terracottahover hover:scale-[1.01] transition duration-300 disabled:opacity-50 disabled:hover:scale-100 inline-flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Processing…
                  </>
                ) : (
                  <>
                    <Lock size={15} />
                    {plan.key === "demo" ? `Book @ ${plan.amount_display}` : `Continue to pay ${plan.amount_display}`}
                  </>
                )}
              </button>
              <p className="font-mono text-[10px] uppercase tracking-widest text-graphite text-center">
                {otpRequired && !otpVerified
                  ? "Verify your phone number above to continue"
                  : "Secured by Razorpay · card & bank details never touch our servers"}
              </p>
              {plan.key === "demo" && (
                <p className="flex items-center justify-center gap-1.5 rounded-xl bg-emerald/10 text-emerald text-[13px] font-bold text-center leading-relaxed px-4 py-3">
                  <CalendarCheck size={15} className="shrink-0" />
                  You can pick your session date and time slot after payment.
                </p>
              )}
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Field({ label, children, testId }) {
  return (
    <label className="block" data-testid={testId}>
      <span className="font-mono text-[10px] uppercase tracking-widest text-graphite mb-2 block">
        {label}
      </span>
      {children}
    </label>
  );
}

function RadioCard({ label, selected, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`w-full flex items-center justify-between gap-3 rounded-lg border px-4 py-3 text-left transition-colors ${
        selected ? "border-emerald bg-emerald/5" : "border-line hover:border-emerald/50"
      }`}
    >
      <span className="text-[14px] font-semibold text-ink">{label}</span>
      <span
        className={`shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
          selected ? "border-emerald" : "border-line"
        }`}
      >
        {selected && <span className="w-2.5 h-2.5 rounded-full bg-emerald" />}
      </span>
    </button>
  );
}
